import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { ScrollReveal } from '@/components/ScrollReveal';
import BackToTop from '@/components/BackToTop';
import StickyReservationCTA from '@/components/StickyReservationCTA';
import { 
  Euro, 
  Bike, 
  Zap, 
  Baby, 
  Users, 
  Building2,
  ChevronDown, 
  ChevronUp,
  MapPin,
  ArrowRight,
  Info,
  CheckCircle,
  Download
} from 'lucide-react';

const Tarifs = () => {
  const [openQuestions, setOpenQuestions] = useState<string[]>([]);
  
  const toggleQuestion = (questionId: string) => {
    setOpenQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId) 
        : [...prev, questionId]
    );
  };

  const tarifs = [
    {
      id: 'classique',
      title: 'Vélo classique',
      icon: Bike,
      price: 'à partir de 8€',
      period: '/ jour',
      description: 'Vélo de ville confortable pour vos balades',
      color: 'bg-blue-100 text-blue-700 border-blue-200'
    },
    {
      id: 'electrique',
      title: 'Vélo électrique',
      icon: Zap,
      price: 'à partir de 15€',
      period: '/ jour',
      description: 'Assistance électrique pour plus de distance',
      color: 'bg-amber-100 text-amber-700 border-amber-200'
    },
    {
      id: 'enfant',
      title: 'Vélo enfant',
      icon: Baby,
      price: 'à partir de 6€',
      period: '/ jour',
      description: 'Vélos adaptés aux enfants, différentes tailles',
      color: 'bg-green-100 text-green-700 border-green-200'
    },
    {
      id: 'saisonnier',
      title: 'Abonnement saisonnier',
      icon: Users,
      price: 'à partir de 30€',
      period: '/ mois',
      description: 'Pour les travailleurs saisonniers',
      color: 'bg-purple-100 text-purple-700 border-purple-200'
    },
    {
      id: 'groupe',
      title: 'Groupes & séminaires',
      icon: Building2,
      price: 'sur devis',
      period: '',
      description: 'Solutions personnalisées pour les groupes',
      color: 'bg-slate-100 text-slate-700 border-slate-200'
    }
  ];

  const faqQuestions = [
    {
      id: 'variation-tarifs',
      q: 'Pourquoi les tarifs varient-ils selon le camping ?',
      a: 'Chaque camping partenaire définit ses propres tarifs en fonction de sa localisation, de ses services et de sa clientèle. Nous nous adaptons à chaque établissement pour proposer des prix cohérents avec le marché local.'
    },
    {
      id: 'accessoires-inclus',
      q: 'Les accessoires sont-ils inclus ?',
      a: 'Oui, l\'antivol et les équipements de base (éclairage, sonnette) sont inclus dans la location. Certains campings proposent des accessoires supplémentaires comme des sièges enfants ou des paniers.'
    },
    {
      id: 'tarifs-degressifs',
      q: 'Proposez-vous des tarifs dégressifs ?',
      a: 'Oui, la plupart de nos campings partenaires proposent des tarifs dégressifs pour les locations de plusieurs jours. Les conditions exactes sont indiquées lors de la réservation.'
    },
    {
      id: 'reservation-avance',
      q: 'Puis-je réserver à l\'avance ?',
      a: 'Oui, nous recommandons même de réserver à l\'avance, surtout en haute saison. Cela garantit la disponibilité des vélos et vous permet de connaître le prix exact de votre location.'
    },
    {
      id: 'prix-exact',
      q: 'Comment connaître le prix exact de ma location ?',
      a: 'Le prix exact s\'affiche lors de la réservation en ligne, une fois que vous avez sélectionné votre camping, vos dates et le type de vélo souhaité. Tous les tarifs sont transparents et sans surprise.'
    }
  ];

  const QuestionAccordion = ({ question, isOpen, onToggle }: { question: any, isOpen: boolean, onToggle: () => void }) => (
    <div className="border border-stone-200 rounded-lg overflow-hidden">
      <button
        className="w-full p-4 text-left hover:bg-stone-50 transition-colors duration-200 flex items-center justify-between"
        onClick={onToggle}
      >
        <span className="font-semibold text-slate-900 pr-4">{question.q}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-slate-600 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-slate-600 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-stone-100">
          <p className="text-slate-700 leading-relaxed pt-3">{question.a}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary to-primary/90 text-primary-foreground py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <div className="w-16 h-16 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Euro className="h-8 w-8" />
            </div>
            <h1 className="text-3xl lg:text-5xl font-bold mb-6">
              Tarifs & conditions de location
            </h1>
            <p className="text-lg lg:text-xl text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto">
              Nos tarifs s'adaptent à chaque lieu et contexte de location.
              Le guide tarifaire complet est disponible en téléchargement ci-dessous.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Logique tarifaire */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
                Comment fonctionnent nos tarifs
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Nos prix s'adaptent à chaque contexte pour offrir le meilleur service
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <ScrollReveal>
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-6">
                  <div className="flex flex-col h-full">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-xl bg-blue-100 text-blue-600 mr-4">
                        <Bike className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900">
                        Location journalière
                      </h3>
                    </div>
                    <p className="text-slate-700 leading-relaxed flex-grow">
                      Tarifs adaptés à chaque camping partenaire selon la zone géographique et les services inclus.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-6">
                  <div className="flex flex-col h-full">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-xl bg-emerald-100 text-emerald-600 mr-4">
                        <Zap className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900">
                        Location hebdomadaire
                      </h3>
                    </div>
                    <p className="text-slate-700 leading-relaxed flex-grow">
                      Tarifs dégressifs pour les séjours plus longs, avec maintenance et assistance incluses.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-6">
                  <div className="flex flex-col h-full">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-xl bg-purple-100 text-purple-600 mr-4">
                        <Users className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900">
                        Abonnements mensuels
                      </h3>
                    </div>
                    <p className="text-slate-700 leading-relaxed flex-grow">
                      Solutions dédiées aux saisonniers et étudiants avec entretien complet inclus.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-6">
                  <div className="flex flex-col h-full">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-xl bg-orange-100 text-orange-600 mr-4">
                        <Building2 className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900">
                        Groupes & séminaires
                      </h3>
                    </div>
                    <p className="text-slate-700 leading-relaxed flex-grow">
                      Tarifs sur mesure selon la taille du groupe, la durée et les services demandés.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </ScrollReveal>
            
            <ScrollReveal delay={400}>
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                <CardContent className="p-6">
                  <div className="flex flex-col h-full">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-xl bg-green-100 text-green-600 mr-4">
                        <CheckCircle className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900">
                        Solutions professionnelles
                      </h3>
                    </div>
                    <p className="text-slate-700 leading-relaxed flex-grow">
                      Partenariats campings et collectivités avec conditions préférentielles et support dédié.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </ScrollReveal>
          </div>

          {/* CTA Principal avec PDF */}
          <ScrollReveal>
            <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-2xl p-8 border border-blue-200">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Guide tarifaire complet
                </h3>
                <p className="text-lg text-slate-700 leading-relaxed mb-6 max-w-2xl mx-auto">
                  Téléchargez notre guide tarifaire détaillé avec tous les prix, conditions et services.
                  Document officiel de référence, imprimable et partageable.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    asChild 
                    size="lg" 
                    className="btn-primary"
                  >
                    <a 
                      href="/documents/guide-tarifaire-les-hirondelles.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Download className="mr-2 h-5 w-5" />
                      Télécharger le guide tarifaire complet (PDF)
                    </a>
                  </Button>
                  
                  <Button 
                    asChild 
                    size="lg" 
                    variant="outline" 
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 font-semibold px-8"
                  >
                    <Link to="/reserver">
                      <MapPin className="mr-2 h-5 w-5" />
                      Voir les prix dans mon camping
                    </Link>
                  </Button>
                </div>
                
                {/* Aperçu visuel du PDF */}
                <div className="mt-8">
                  <a 
                    href="/documents/guide-tarifaire-les-hirondelles.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    <div className="relative overflow-hidden rounded-xl border-2 border-slate-200 group-hover:border-blue-300 transition-all duration-300 shadow-lg group-hover:shadow-xl max-w-2xl mx-auto">
                      <img 
                        src="./images/pricing_guide_preview_20260121_103746.png" 
                        alt="Aperçu du guide tarifaire Les Hirondelles"
                        className="w-full h-auto group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-4">
                          <Download className="h-8 w-8 text-blue-600" />
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 text-center mt-4 group-hover:text-blue-600 transition-colors">
                      Cliquez pour ouvrir le guide tarifaire complet
                    </p>
                  </a>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Mini FAQ Tarifs */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
                Questions fréquentes sur les tarifs
              </h2>
              <p className="text-lg text-slate-600">
                Tout ce que vous devez savoir sur nos prix
              </p>
            </div>
          </ScrollReveal>

          <div className="space-y-4">
            {faqQuestions.map((question, index) => (
              <ScrollReveal key={question.id} delay={index * 100}>
                <QuestionAccordion
                  question={question}
                  isOpen={openQuestions.includes(question.id)}
                  onToggle={() => toggleQuestion(question.id)}
                />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Prêt à connaître vos tarifs exacts ?
            </h2>
            <p className="text-lg text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
              Sélectionnez votre camping pour voir les prix en temps réel et réserver directement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                variant="secondary" 
                className="bg-white text-primary hover:bg-white/90 font-semibold px-8"
              >
                <Link to="/reserver">
                  <MapPin className="mr-2 h-5 w-5" />
                  Réserver un vélo
                </Link>
              </Button>
              <Button 
                asChild 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 font-semibold px-8"
              >
                <Link to="/faq">
                  Toutes les questions fréquentes
                </Link>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <StickyReservationCTA />
      <BackToTop />
      <Footer />
    </div>
  );
};

export default Tarifs;